import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useAgent } from "@/contexts/AgentContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Sparkles, 
  Zap, 
  TrendingUp, 
  AlertTriangle, 
  Lightbulb,
  Check,
  X,
  ChevronRight,
  Brain,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { FeedbackButtons } from "./FeedbackButtons";

interface ProactiveInsight {
  id: string;
  insight_type: string;
  title: string;
  description: string;
  priority: string;
  confidence: number;
  action_type?: string | null;
  action_data?: any;
  status: string;
  created_at: string;
}

interface PatternDetection {
  id: string;
  pattern_type: string;
  pattern_data: any;
  occurrences: number;
  confidence: number;
  suggested_automation?: any;
}

interface ProactiveAgentProps {
  workspaceId?: string;
  tableId?: string;
  compact?: boolean;
}

export const ProactiveAgent = ({ workspaceId, tableId, compact = false }: ProactiveAgentProps) => {
  const { user } = useAuth();
  const { logInteraction, recordFeedback } = useAgent();
  const [insights, setInsights] = useState<ProactiveInsight[]>([]);
  const [patterns, setPatterns] = useState<PatternDetection[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    if (user) {
      fetchInsights();
      subscribeToInsights();
    }
  }, [user]);

  const fetchInsights = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data: insightsData } = await supabase
        .from("proactive_insights")
        .select("*")
        .eq("user_id", user.id)
        .eq("status", "pending")
        .order("priority", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(10);

      setInsights(insightsData || []);

      const { data: patternsData } = await supabase
        .from("pattern_detections")
        .select("*")
        .eq("user_id", user.id)
        .gte("confidence", 0.7)
        .order("confidence", { ascending: false })
        .limit(5);

      setPatterns(patternsData || []);
    } catch (error) {
      console.error("Failed to fetch insights:", error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToInsights = () => {
    if (!user) return;

    const channel = supabase
      .channel("proactive-insights")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "proactive_insights",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          setInsights((prev) => [payload.new as ProactiveInsight, ...prev]);
          // Show toast for high-priority insights
          if ((payload.new as ProactiveInsight).priority === "high") {
            toast.info((payload.new as ProactiveInsight).title, {
              description: (payload.new as ProactiveInsight).description,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const triggerAnalysis = async () => {
    if (!user) return;
    
    setAnalyzing(true);
    try {
      // Call agent to analyze current context
      const { data, error } = await supabase.functions.invoke("agent-think", {
        body: {
          operation: "suggest",
          tableId,
          context: { workspaceId, triggeredManually: true },
        },
      });

      if (error) throw error;

      if (data?.suggestions?.length > 0) {
        toast.success(`Found ${data.suggestions.length} new insights!`);
        fetchInsights();
      } else {
        toast.info("No new insights at this time");
      }
    } catch (error) {
      console.error("Analysis failed:", error);
      toast.error("Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleInsightAction = async (insight: ProactiveInsight, action: "accept" | "dismiss") => {
    try {
      const { error } = await supabase
        .from("proactive_insights")
        .update({
          status: action === "accept" ? "acted" : "dismissed",
          acted_at: action === "accept" ? new Date().toISOString() : null,
          dismissed_at: action === "dismiss" ? new Date().toISOString() : null,
        })
        .eq("id", insight.id);

      if (error) throw error;

      setInsights((prev) => prev.filter((i) => i.id !== insight.id));

      // Log for learning
      await logInteraction(
        "insight_response",
        { insightId: insight.id, insightType: insight.insight_type },
        { action },
        action === "accept" ? "success" : "dismissed"
      );

      if (action === "accept" && insight.action_type) {
        toast.success(`Action "${insight.action_type}" initiated`);
        // Execute the action based on type
        await executeInsightAction(insight);
      }
    } catch (error) {
      console.error("Failed to update insight:", error);
      toast.error("Failed to update");
    }
  };

  const executeInsightAction = async (insight: ProactiveInsight) => {
    switch (insight.action_type) {
      case "create_automation":
        // Navigate to automation builder with prefilled data
        window.dispatchEvent(new CustomEvent("create-automation", { 
          detail: insight.action_data 
        }));
        break;
      case "create_view":
        // Create a new view
        toast.info("Creating view...");
        break;
      case "send_notification":
        // Already handled by creating the insight
        break;
      default:
        break;
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case "automation_suggestion":
        return <Zap className="h-4 w-4" />;
      case "trend_detected":
        return <TrendingUp className="h-4 w-4" />;
      case "anomaly_alert":
        return <AlertTriangle className="h-4 w-4" />;
      case "optimization":
        return <Lightbulb className="h-4 w-4" />;
      default:
        return <Sparkles className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-destructive/10 text-destructive border-destructive/30";
      case "medium":
        return "bg-yellow-500/10 text-yellow-600 border-yellow-500/30";
      case "low":
        return "bg-primary/10 text-primary border-primary/30";
      default:
        return "bg-muted";
    }
  };

  if (compact) {
    if (insights.length === 0 && patterns.length === 0) {
      return null;
    }

    return (
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={triggerAnalysis}
          disabled={analyzing}
          className="gap-1.5"
        >
          {analyzing ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <Brain className="h-4 w-4" />
          )}
          <span className="hidden sm:inline">
            {insights.length > 0 ? `${insights.length} Insights` : "Analyze"}
          </span>
        </Button>
        {insights.length > 0 && (
          <Badge variant="secondary" className="bg-primary/10 text-primary">
            {insights.length}
          </Badge>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Loading insights...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-primary/50 flex items-center justify-center">
              <Brain className="h-4 w-4 text-primary-foreground" />
            </div>
            <div>
              <CardTitle className="text-base">Proactive Intelligence</CardTitle>
              <CardDescription className="text-xs">AI-powered insights and suggestions</CardDescription>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={triggerAnalysis}
            disabled={analyzing}
            className="gap-1.5"
          >
            {analyzing ? (
              <>
                <RefreshCw className="h-3 w-3 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="h-3 w-3" />
                Analyze
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {insights.length === 0 && patterns.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground">
            <Sparkles className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No pending insights</p>
            <p className="text-xs">Click Analyze to scan for patterns</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[400px]">
            <div className="divide-y">
              {/* Insights */}
              {insights.map((insight) => (
                <div
                  key={insight.id}
                  className="p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "h-8 w-8 rounded-lg flex items-center justify-center shrink-0",
                      getPriorityColor(insight.priority)
                    )}>
                      {getInsightIcon(insight.insight_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{insight.title}</span>
                        <Badge variant="outline" className={cn("text-[10px]", getPriorityColor(insight.priority))}>
                          {insight.priority}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        {insight.description}
                      </p>
                      <div className="flex items-center gap-2">
                        {insight.action_type && (
                          <Button
                            size="sm"
                            className="h-7 text-xs gap-1"
                            onClick={() => handleInsightAction(insight, "accept")}
                          >
                            <Check className="h-3 w-3" />
                            {insight.action_type.replace(/_/g, " ")}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={() => handleInsightAction(insight, "dismiss")}
                        >
                          <X className="h-3 w-3" />
                          Dismiss
                        </Button>
                        <span className="text-[10px] text-muted-foreground ml-auto">
                          {Math.round(insight.confidence * 100)}% confident
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Detected Patterns */}
              {patterns.length > 0 && (
                <div className="p-3 bg-muted/30">
                  <div className="flex items-center gap-2 mb-2 text-xs font-medium text-muted-foreground">
                    <TrendingUp className="h-3 w-3" />
                    Detected Patterns
                  </div>
                  <div className="space-y-2">
                    {patterns.map((pattern) => (
                      <div
                        key={pattern.id}
                        className="flex items-center justify-between p-2 bg-background rounded-lg text-xs"
                      >
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-[10px]">
                            {pattern.pattern_type}
                          </Badge>
                          <span className="text-muted-foreground">
                            {pattern.occurrences}x detected
                          </span>
                        </div>
                        {pattern.suggested_automation && (
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1">
                            Automate <ChevronRight className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};
